import { api } from './api';

export interface SyncResult {
  success: boolean;
  data: Record<string, unknown>;
  message: string;
}

export interface PeopleForceStatus {
  configured: boolean;
  success: boolean;
  message: string;
  employeeCount?: number;
}

export interface SyncHistoryEntry {
  id: string;
  syncType: string;
  status: string;
  recordsProcessed: number;
  recordsFailed: number;
  message: string | null;
  syncedAt: string;
}

export async function getPeopleForceStatus(): Promise<PeopleForceStatus> {
  const { data } = await api.get<PeopleForceStatus>('/peopleforce/status');
  return data;
}

export async function getSyncHistory(limit = 10): Promise<SyncHistoryEntry[]> {
  const { data } = await api.get<{ success: boolean; data: SyncHistoryEntry[] }>(
    '/peopleforce/sync/history',
    { params: { limit } }
  );
  return data.data;
}

export async function syncEmployees(): Promise<SyncResult> {
  const { data } = await api.post<SyncResult>('/peopleforce/sync-employees');
  return data;
}

export async function syncAvatars(): Promise<SyncResult> {
  const { data } = await api.post<SyncResult>('/peopleforce/sync-avatars');
  return data;
}

export interface AvatarStatus {
  totalEmployees: number;
  withLocalAvatar: number;
  localUrlMissingFile: number;
  withExternalAvatar: number;
  withoutAvatar: number;
}

export async function getAvatarStatus(): Promise<AvatarStatus> {
  const { data } = await api.get<AvatarStatus>('/peopleforce/avatars/status');
  return data;
}

export interface AvatarPullResult {
  totalScanned: number;
  pulled: number;
  skippedAlreadyLocal: number;
  skippedNoPfAvatar: number;
  skippedDefault: number;
  failed: number;
  errors: string[];
}

export async function pullAvatarsFromPf(): Promise<SyncResult> {
  const { data } = await api.post<SyncResult>('/peopleforce/avatars/pull-from-pf');
  return data;
}

export async function syncCalendar(): Promise<SyncResult> {
  const { data } = await api.post<SyncResult>('/peopleforce/sync-calendar');
  return data;
}

export async function syncCompensations(): Promise<SyncResult> {
  const { data } = await api.post<SyncResult>('/peopleforce/sync-compensations');
  return data;
}

export async function syncAdditionalCompensations(): Promise<SyncResult> {
  const { data } = await api.post<SyncResult>('/peopleforce/sync-additional-compensations');
  return data;
}

export async function syncVacancies(): Promise<SyncResult> {
  const { data } = await api.post<SyncResult>('/peopleforce/sync-vacancies');
  return data;
}

export async function syncCandidates(): Promise<SyncResult> {
  const { data } = await api.post<SyncResult>('/peopleforce/sync-candidates');
  return data;
}

export interface BackfillCandidateNotesCvsResult {
  candidatesScanned: number;
  candidatesTotal: number;
  cvsCreated: number;
  cvsSkipped: number;
  cvsErrors: number;
  notesCreated: number;
  notesUpdated: number;
  notesSkippedEditedLocally: number;
  notesErrors: number;
  errorMessages: string[];
  durationMs: number;
}

export interface BackfillCandidateNotesCvsResponse {
  success: boolean;
  data: BackfillCandidateNotesCvsResult;
  message: string;
}

/**
 * Live snapshot of the bulk backfill — polled by the UI to drive a progress bar.
 * Returns `null` when no backfill has been started since the server booted.
 */
export interface BackfillProgressSnapshot {
  status: 'running' | 'completed' | 'partial' | 'failed';
  monthsBack: number;
  startedAt: string;
  finishedAt: string | null;
  total: number;
  processed: number;
  cvsCreated: number;
  cvsSkipped: number;
  cvsErrors: number;
  notesCreated: number;
  notesUpdated: number;
  notesSkippedEditedLocally: number;
  notesErrors: number;
  currentCandidate: { id: string; externalId: string | null; fullName: string } | null;
  durationMs: number;
  error: string | null;
}

export async function getBackfillStatus(): Promise<BackfillProgressSnapshot | null> {
  const { data } = await api.get<BackfillProgressSnapshot | null>(
    '/peopleforce/backfill-status'
  );
  return data;
}

export interface SingleCandidateBackfillResult {
  candidateId: string;
  externalId: string;
  cvsCreated: number;
  cvsSkipped: number;
  cvsErrors: number;
  notesCreated: number;
  notesUpdated: number;
  notesSkippedEditedLocally: number;
  notesErrors: number;
  errorMessages: string[];
}

export interface SingleCandidateBackfillResponse {
  success: boolean;
  data: SingleCandidateBackfillResult;
  message: string;
}

/**
 * Per-candidate "Pull from PeopleForce" — pulls notes + CV for ONE candidate.
 * Fast feedback alternative to the bulk backfill: returns in 1-3 seconds
 * instead of 30+ minutes.
 */
export async function backfillSingleCandidateFromPf(
  candidateId: string
): Promise<SingleCandidateBackfillResponse> {
  const { data } = await api.post<SingleCandidateBackfillResponse>(
    `/peopleforce/candidates/${candidateId}/backfill-notes-cvs`
  );
  return data;
}

/**
 * One-shot migration: backfill candidate notes and CVs from PeopleForce.
 * Bounded by `monthsBack` (default 6) — only touches candidates whose PF
 * `updated_at` falls inside that window, so we don't pay for the 40k dead tail.
 *
 * Note: body is intentionally `undefined` (not `null`). The global NestJS
 * `ValidationPipe` with `forbidNonWhitelisted: true` rejects a literal `null`
 * JSON body with 400 when there's no `@Body` DTO to whitelist against —
 * sending no body at all bypasses the body parser entirely.
 */
export async function backfillCandidateNotesCvs(
  opts: { monthsBack?: number } = {}
): Promise<BackfillCandidateNotesCvsResponse> {
  const params = opts.monthsBack !== undefined ? { monthsBack: opts.monthsBack } : undefined;
  const { data } = await api.post<BackfillCandidateNotesCvsResponse>(
    '/peopleforce/backfill-candidate-notes-cvs',
    undefined,
    { params }
  );
  return data;
}

// =====================================================================
// Employee Documents backfill from PeopleForce (mirror of candidate CVs).
// =====================================================================

/** Live snapshot for the bulk employee-documents backfill — UI polls this. */
export interface DocumentBackfillProgress {
  status: 'running' | 'completed' | 'partial' | 'failed';
  startedAt: string;
  finishedAt: string | null;
  total: number;          // employees in the target set
  processed: number;       // employees fully processed
  documentsCreated: number;
  documentsSkipped: number;
  documentsErrors: number;
  currentEmployee: { id: string; workEmail: string | null } | null;
  durationMs: number;
  error: string | null;
}

export async function getDocumentsBackfillStatus(): Promise<DocumentBackfillProgress | null> {
  const { data } = await api.get<DocumentBackfillProgress | null>(
    '/peopleforce/documents-backfill-status'
  );
  return data;
}

/**
 * Bulk employee-documents backfill — iterates every employee with a PF id,
 * downloads + encrypts every document not already on disk. Same caveats as
 * the candidate variant: long-running, kills on backend restart, idempotent
 * on re-run, body intentionally `undefined`.
 */
export async function backfillEmployeeDocuments(): Promise<SyncResult> {
  const { data } = await api.post<SyncResult>(
    '/peopleforce/migrate-documents',
    undefined
  );
  return data;
}

export interface SingleEmployeeDocumentsBackfillResult {
  employeeId: string;
  workEmail: string | null;
  documentsCreated: number;
  documentsSkipped: number;
  documentsErrors: number;
  errorMessages: string[];
  durationMs: number;
}

export interface SingleEmployeeDocumentsBackfillResponse {
  success: boolean;
  data: SingleEmployeeDocumentsBackfillResult;
  message: string;
}

/**
 * Per-employee "Pull docs from PeopleForce" — backfills documents for ONE
 * employee. Returns in seconds. Powers the button on the Documents tab.
 */
export async function backfillSingleEmployeeDocumentsFromPf(
  employeeId: string
): Promise<SingleEmployeeDocumentsBackfillResponse> {
  const { data } = await api.post<SingleEmployeeDocumentsBackfillResponse>(
    `/peopleforce/employees/${employeeId}/backfill-documents`
  );
  return data;
}

export async function syncAbsenceBalances(): Promise<SyncResult> {
  const { data } = await api.post<SyncResult>('/peopleforce/sync-absence-balances');
  return data;
}

export async function syncAbsenceTypes(): Promise<SyncResult> {
  const { data } = await api.post<SyncResult>('/peopleforce/sync-absence-types');
  return data;
}

export interface PaginatedResponse<T> {
  data: T[];
  metadata: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface VacancyPreview {
  id: string;
  peopleForceId: number;
  title: string;
  description: string | null;
  state: string;
  priority: string | null;
  positions: number;
  minSalary: number | null;
  maxSalary: number | null;
  currencyCode: string | null;
  employmentType: string | null;
  jobLevel: string | null;
  applicationsCount: number;
  postOnCareers: boolean;
  department: { id: string; name: string } | null;
  location: { id: string; name: string } | null;
  hiringLead: { id: string; firstName: string; lastName: string } | null;
  pipelineData: { id: number; name: string } | null;
  tags: { id: number; name: string }[] | null;
  skills: { id: number; name: string }[] | null;
  collaborators: { id: number; first_name: string; last_name: string }[] | null;
  openedAt: string | null;
  startsOn: string | null;
  endsOn: string | null;
  pfCreatedAt: string;
  pfUpdatedAt: string;
}

export interface CandidatePreview {
  id: string;
  peopleForceId: number;
  fullName: string;
  email: string | null;
  phone: string | null;
  source: string | null;
  coverLetter: string | null;
  hasResume: boolean;
  desiredSalary: number | null;
  currencyCode: string | null;
  urls: string[] | null;
  skills: { id: number; name: string }[] | null;
  languages: { id: number; name: string; level?: string }[] | null;
  tags: { id: number; name: string }[] | null;
  position: string | null;
  location: string | null;
  lastContactedAt: string | null;
  applications: {
    id: string;
    pipelineStageName: string | null;
    pipelineStageId: number | null;
    disqualifiedAt: string | null;
    disqualifyReason: string | null;
    vacancy: { id: string; title: string; state: string };
    pfCreatedAt: string;
    pfUpdatedAt: string;
  }[];
  createdBy: { id: string; firstName: string; lastName: string } | null;
  pfCreatedAt: string;
  pfUpdatedAt: string;
}

export async function getVacancies(
  page = 1,
  limit = 50,
  state?: string,
  search?: string
): Promise<PaginatedResponse<VacancyPreview>> {
  const params: Record<string, string | number> = { page, limit };
  if (state) params.state = state;
  if (search) params.search = search;
  const { data } = await api.get<PaginatedResponse<VacancyPreview>>('/peopleforce/vacancies', {
    params,
  });
  return data;
}

export async function getCandidates(
  page = 1,
  limit = 50,
  vacancyId?: string,
  search?: string
): Promise<PaginatedResponse<CandidatePreview>> {
  const params: Record<string, string | number | undefined> = { page, limit, vacancyId };
  if (search) params.search = search;
  const { data } = await api.get<PaginatedResponse<CandidatePreview>>('/peopleforce/candidates', {
    params,
  });
  return data;
}

export interface RecruitmentCounts {
  vacancies: number;
  candidates: number;
  applications: number;
}

export async function getRecruitmentCounts(): Promise<RecruitmentCounts> {
  const [vac, cand] = await Promise.all([getVacancies(1, 1), getCandidates(1, 1)]);
  return {
    vacancies: vac.metadata.total,
    candidates: cand.metadata.total,
    applications: 0,
  };
}

export async function syncProjects(): Promise<SyncResult> {
  const { data } = await api.post<{ success: boolean; data: Record<string, unknown> }>(
    '/sync/projects/run'
  );
  const d = data.data as Record<string, number>;
  const parts: string[] = [];
  if (d.projectsCreated > 0) parts.push(`${d.projectsCreated} created`);
  if (d.projectsUpdated > 0) parts.push(`${d.projectsUpdated} updated`);
  if (d.membersAdded > 0) parts.push(`${d.membersAdded} members added`);
  if (d.membersRemoved > 0) parts.push(`${d.membersRemoved} members removed`);
  return {
    success: data.success,
    data: data.data,
    message: parts.length > 0 ? `Synced: ${parts.join(', ')}` : 'No changes',
  };
}
